import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { rawRequest, useSession } from "../../../packages/api/client";
import { Button, ErrorNotice } from "../../../packages/ui";

const encode = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
export async function startFlowLogin(origin: string, returnTo?: string) {
  const verifier = encode(crypto.getRandomValues(new Uint8Array(32)));
  const state = encode(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = encode(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)),
    ),
  );
  sessionStorage.setItem(
    "huiju-flow-login",
    JSON.stringify({
      verifier,
      state,
      createdAt: Date.now(),
      returnTo: returnTo || (location.hash.startsWith("#/flow?")
        ? location.hash.slice(1)
        : "/courses"),
    }),
  );
  const url = new URL("/asymp-flow/huiju-connect.html", origin);
  url.searchParams.set("state", state);
  url.searchParams.set("challenge", challenge);
  location.assign(url.href);
}
export function FlowAutoLogin() {
  const [params] = useSearchParams();
  const started = useRef(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  const connect = async () => {
    setError("");
    setBusy(true);
    try {
      const config = await rawRequest<{ enabled: boolean; origin: string }>("/flow/config", "");
      if (!config.enabled || config.origin !== "https://hjoi.com.cn") throw new Error("当前机构未开通 Flow 账号登录");
      const classId = params.get("class") || "";
      await startFlowLogin(config.origin, /^[a-f0-9]{24}$/.test(classId) ? `/flow?class=${classId}` : "/courses");
    } catch (e) {
      setError(e instanceof Error ? e.message : "账号连接失败，请重试");
      setBusy(false);
    }
  };
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void connect();
  }, []);
  return <main className="login-page"><section className="login-card"><h1>正在进入教学流</h1><p>正在连接你已登录的 Flow 账号…</p><ErrorNotice error={error} />{!busy && <Button onClick={() => void connect()}>重新连接</Button>}</section></main>;
}
export function FlowLoginButton() {
  const [config, setConfig] = useState<{
    enabled: boolean;
    origin: string;
  } | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let mounted = true;
    rawRequest<{ enabled: boolean; origin: string }>("/flow/config", "")
      .then((c) => {
        if (mounted) setConfig(c);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);
  if (!config?.enabled) return null;
  return (
    <>
      <ErrorNotice error={error} />
      <Button
        variant="primary"
        onClick={() =>
          void startFlowLogin(config.origin).catch((e) => setError(e.message))
        }
      >
        使用 Flow 账号登录
      </Button>
    </>
  );
}
export function FlowLoginCallback() {
  const [params] = useSearchParams();
  const { login } = useSession();
  const navigate = useNavigate();
  const started = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const finish = async () => {
      const saved = JSON.parse(
        sessionStorage.getItem("huiju-flow-login") || "null",
      );
      if (
        !saved ||
        saved.state !== params.get("state") ||
        Date.now() - saved.createdAt > 10 * 60_000
      )
        throw new Error("登录校验已失效，请重新从 Flow 进入。");
      const response = await rawRequest<{ token: string }>(
        "/flow/exchange",
        "",
        {
          method: "POST",
          body: JSON.stringify({
            code: params.get("code"),
            state: saved.state,
            verifier: saved.verifier,
          }),
        },
      );
      sessionStorage.removeItem("huiju-flow-login");
      login(response.token);
      navigate(
        typeof saved.returnTo === "string" &&
          saved.returnTo.startsWith("/flow?")
          ? saved.returnTo
          : "/courses",
        { replace: true },
      );
    };
    void finish().catch((e) => setError(e.message));
  }, [params, login, navigate]);
  return (
    <div className="auth-error">
      <ErrorNotice error={error} />
      {error ? (
        <Button onClick={() => navigate("/flow", { replace: true })}>
          返回登录
        </Button>
      ) : (
        <span>正在连接教学空间…</span>
      )}
    </div>
  );
}
